import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { logQuietError } from "@/lib/quiet-error";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Library as LibIcon,
  Search,
  Sparkles,
  FileText,
  ScanLine,
  PenLine,
  ArrowRight,
  Globe,
  ListChecks,
  Layers,
} from "lucide-react";
import { SkeletonRow, Skeleton } from "@/components/Skeleton";

const KIND_META = {
  lesson:     { icon: PenLine,  chip: "tb-chip-lavender", label: "Lesson" },
  generation: { icon: Sparkles, chip: "tb-chip-mint",     label: "AI generation" },
  document:   { icon: ScanLine, chip: "tb-chip-butter",   label: "Document" },
};

const FILTERS = [
  { key: "all",         label: "All",         icon: LibIcon },
  { key: "lessons",     label: "Lessons",     icon: PenLine },
  { key: "generations", label: "Generations", icon: Sparkles },
  { key: "documents",   label: "Documents",   icon: ScanLine },
];

export default function LibraryPage() {
  const navigate = useNavigate();
  const [type, setType] = useState("all");
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setItems(null);
      try {
        const { data } = await api.get(`/library/items?type=${type}${q ? `&q=${encodeURIComponent(q)}` : ""}`);
        if (!alive) return;
        setItems(data.items);
        setCounts(data.counts);
      } catch (e) {
        logQuietError("library.items", e);
        if (alive) setItems([]);
      }
    })();
    return () => { alive = false; };
  }, [type, q]);

  return (
    <Layout title="Library">
      <div className="mb-6">
        <span className="tb-chip-mint inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <LibIcon className="h-3 w-3" /> Your content
        </span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
          Your library
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
          Every lesson, AI generation and scanned document — in one searchable canvas.
        </p>
      </div>

      {/* Search + filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6 items-stretch sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-11 rounded-full"
            data-testid="library-search"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto tb-no-scrollbar">
          {FILTERS.map((f) => {
            const Icon = f.icon;
            const active = type === f.key;
            const count = f.key === "all" ? counts.total : counts[f.key];
            return (
              <button
                key={f.key}
                onClick={() => setType(f.key)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition border ${
                  active ? "bg-primary text-primary-foreground border-primary" : "bg-card text-foreground border-border hover:border-primary/40"
                }`}
                data-testid={`library-filter-${f.key}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {f.label}
                {count !== undefined && <span className="ml-1 text-[10px] opacity-70">{count}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {items === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="tb-card p-5"><SkeletonRow /></div>)}
        </div>
      ) : items.length === 0 ? (
        <div className="tb-card p-10 text-center" data-testid="library-empty">
          <span className="mx-auto h-14 w-14 grid place-items-center rounded-full border border-dashed border-border mb-3">
            <LibIcon className="h-6 w-6 text-muted-foreground" />
          </span>
          <p className="font-display text-lg font-medium">Nothing here yet</p>
          <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
            Use "Teach from anything" or the AI tools to generate your first lesson — it'll land here automatically.
          </p>
          <Button className="mt-4 rounded-full" onClick={() => navigate("/teach-from-anything")} data-testid="library-empty-cta">
            <Sparkles className="h-4 w-4 mr-1.5" /> Teach from anything
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 tb-stagger">
          {items.map((it) => {
            const meta = KIND_META[it.kind] || KIND_META.generation;
            const Icon = meta.icon;
            return (
              <button
                key={`${it.kind}-${it.id}`}
                onClick={() => navigate(it.open_path)}
                className="tb-card-interactive p-5 text-left"
                data-testid={`library-item-${it.kind}-${it.id}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`inline-grid place-items-center h-7 w-7 rounded-lg ${meta.chip}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">{meta.label}</span>
                  {it.meta?.is_published && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-primary">
                      <Globe className="h-3 w-3" /> Public
                    </span>
                  )}
                </div>
                <p className="mt-3 font-display font-medium text-base leading-snug line-clamp-2">{it.title || "Untitled"}</p>
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  {[it.subject, it.grade && `Grade ${it.grade}`, it.language].filter(Boolean).join(" · ") || (it.kind === "generation" ? (it.meta?.gen_kind || "").replace(/_/g, " ") : "—")}
                </p>
                <div className="mt-4 inline-flex items-center gap-1 text-xs text-primary">
                  Open <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
